import {
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import moment from "moment";
import React from "react";
import NumberFormat from "react-number-format";
import { DoneIcon } from "./StepIcons";
import TransactionHistoryBase from "./TransactionHistoryBase";
import styles from "./styles";
import "./style.scss";
import {
  decideTransactionMethod,
  getCompletedRelated,
  isCorrectionsExist,
} from "./utils";

const ManualAdjustment = ({ details }) => {
  return (
    <Stepper orientation="vertical" style={{ padding: 0 }}>
      {details[0].adjustments
        .reverse()
        .map(({ completeUnit, completeUnitPrice, navDate }, index) => (
          <Step active key={index}>
            <StepLabel icon={<DoneIcon active />}>
              <Typography
                color="textPrimary"
                style={{
                  ...styles.baseFont,
                  ...styles.label,
                }}
              >
                Unit Price Adjustment
              </Typography>
            </StepLabel>
            <StepContent>
              <Typography
                style={{
                  ...styles.baseFont,
                  ...styles.content,
                }}
              >
                Unit Price is adjusted to reflect the fund's value.
              </Typography>
              <div
                style={{
                  marginTop: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  style={{
                    ...styles.baseFont,
                    ...styles.content,
                  }}
                >
                  Unit: {completeUnit}
                </Typography>

                <Typography
                  style={{
                    ...styles.baseFont,
                    ...styles.content,
                  }}
                >
                  Unit Price:{" "}
                  <NumberFormat
                    prefix="RM"
                    value={parseFloat(completeUnitPrice)}
                    thousandSeparator
                    displayType="text"
                    decimalScale={4}
                    fixedDecimalScale
                  />
                </Typography>
              </div>
              <Typography
                style={{
                  ...styles.baseFont,
                  ...styles.content,
                  ...styles.date,
                  marginTop: "8px",
                }}
              >
                {moment(navDate)
                  .utcOffset("+0800")
                  .format("DD MMM YYYY")}
              </Typography>
            </StepContent>
          </Step>
        ))}
    </Stepper>
  );
};

const SalesCharge = ({
  salesChargeAmount,
  salesChargeCcy,
  salesChargePercent,
  serviceTaxRate,
  serviceTaxAmount,
  amount,
  fee,
  isRetire,
  amountOnly,
  ppaFee,
}) => {
  return (
    <>
      <div
        style={{
          marginTop: "3px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Typography
          style={{
            ...styles.baseFont,
            ...styles.content,
          }}
        >
          Total Cash In
        </Typography>
        <Typography
          style={{
            ...styles.baseFont,
            ...styles.content,
          }}
        >
          <NumberFormat
            prefix="RM"
            value={(isRetire ? amountOnly : amount) + fee}
            thousandSeparator
            displayType="text"
            decimalScale={2}
            fixedDecimalScale
          />
        </Typography>
      </div>

      {Boolean(fee && fee > 0) && (
        <div
          style={{
            marginTop: "3px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography
            style={{
              ...styles.baseFont,
              ...styles.content,
            }}
          >
            Administration Fee
          </Typography>
          <Typography
            style={{
              ...styles.baseFont,
              ...styles.content,
            }}
          >
            <NumberFormat
              prefix="RM"
              value={fee}
              thousandSeparator
              displayType="text"
              decimalScale={2}
              fixedDecimalScale
            />
          </Typography>
        </div>
      )}
      {isRetire && (
        <div className="ppa-cont">
          <div>
            <p>Private Pension</p>
            <p>Administrator Fee</p>
          </div>

          <p>
            <NumberFormat
              prefix="RM"
              value={ppaFee || 0}
              thousandSeparator
              displayType="text"
              decimalScale={2}
              fixedDecimalScale
            />
          </p>
        </div>
      )}
      <div
        style={{
          marginTop: "3px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Typography
          style={{
            ...styles.baseFont,
            ...styles.content,
          }}
        >
          Sales Charge ({salesChargePercent || 0}%)
        </Typography>
        <Typography
          style={{
            ...styles.baseFont,
            ...styles.content,
          }}
        >
          <NumberFormat
            prefix="RM"
            value={salesChargeAmount || 0}
            thousandSeparator
            displayType="text"
            decimalScale={2}
            fixedDecimalScale
          />
        </Typography>
      </div>
      {/* START: SST */}
      {
        <div
          style={{
            marginTop: "3px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography
            style={{
              ...styles.baseFont,
              ...styles.content,
            }}
          >
            SST ({serviceTaxRate || 0}%)
          </Typography>
          <Typography
            style={{
              ...styles.baseFont,
              ...styles.content,
            }}
          >
            <NumberFormat
              prefix="RM"
              value={serviceTaxAmount || 0}
              thousandSeparator
              displayType="text"
              decimalScale={2}
              fixedDecimalScale
            />
          </Typography>
        </div>
      }
      {/* END: SST */}
      <div
        style={{
          marginTop: "3px",
          display: "flex",
          justifyContent: "space-between",
          fontWeight: "700",
          color: "#616367",
        }}
      >
        <Typography
          style={{
            ...styles.baseFont,
            ...styles.content,
            fontWeight: "inherit",
            color: "inherit",
          }}
        >
          Net amount
        </Typography>
        <Typography
          style={{
            ...styles.baseFont,
            ...styles.content,
            fontWeight: "inherit",
            color: "inherit",
          }}
        >
          <NumberFormat
            prefix="RM"
            value={isRetire ? amount : amount - salesChargeAmount}
            thousandSeparator
            displayType="text"
            decimalScale={2}
            fixedDecimalScale
          />
        </Typography>
      </div>
    </>
  );
};

const Buy = ({
  status,
  method,
  amount,
  unit,
  fee,
  submittedDate,
  processingDate,
  completedDate,
  details,
  isRsp,
  salesChargeAmount,
  salesChargeCcy,
  salesChargePercent,
  planId,
  amountOnly,
  ppaFee,
  transactionType,
  isManBuy,
}) => {
  const completed = getCompletedRelated({
    status,
    isDoneIconActive: status === "5" || status === "10",
  });
  const frontText = isRsp ? "Your Auto" : "Your";

  const isFailed = status === "9";
  const isRetire = planId === "P11";

  const isSubmitted =
    status === "3" ||
    status === "4" ||
    status === "5" ||
    status === "6" ||
    status === "9" ||
    status === "10" ||
    status === "11";

  const isProcessing =
    status === "4" ||
    status === "5" ||
    status === "9" ||
    status === "10" ||
    status === "11" ||
    (status === "3" && isRetire); //special case for mpRetire, status 3 treat as processing

  const isCompleted =
    status === "5" || status === "9" || status === "10" || status === "11";

  const corrections = isCorrectionsExist(details);
  const { serviceTaxAmount = 0, serviceTaxRate = 0 } =
    (Array.isArray(details) && details.at(0)) || {};

  return (
    <TransactionHistoryBase
      manualAdjustment={
        details.length > 0 && details[0].adjustments.length > 0 ? (
          <ManualAdjustment details={details} />
        ) : null
      }
    >
      {/* Submitted */}
      <Step active={isSubmitted}>
        <StepLabel
          icon={<DoneIcon active={isSubmitted} />}
          sx={{ padding: "0" }}
        >
          <Typography
            color="textPrimary"
            style={{ ...styles.baseFont, ...styles.label }}
          >
            Submitted
          </Typography>
        </StepLabel>
        <StepContent
          style={{
            borderColor: isSubmitted && "#0091DA",
            borderWidth: 2,
            height: "100%",
            paddingBottom: "24px",
          }}
        >
          <Typography style={{ ...styles.baseFont, ...styles.content }}>
            Your {isRsp && " Auto"} Cash In amount has been successfully
            submitted.
          </Typography>
          <Typography
            style={{
              ...styles.baseFont,
              ...styles.content,
              marginTop: "8px",
            }}
          >
            Transaction Method:{" "}
            {decideTransactionMethod({ method, transactionType })}
          </Typography>
          {
            details.length > 0 && details[0]?.campaignCode && (
              <Typography
                style={{
                  ...styles.baseFont,
                  ...styles.content,
                  marginTop: "8px",
                  color: "#18a29a"
                }}
              >
                Promo Code: {details[0].campaignCode}
              </Typography>
            )
          }

          {/* only show this if is rsp OR submitted and processing */}
          {isRsp && (
            <Typography
              style={{
                ...styles.baseFont,
                ...styles.content,
                marginTop: "8px",
              }}
            >
              Administration Fee:
              <NumberFormat
                prefix="RM"
                value={fee}
                thousandSeparator
                displayType="text"
                decimalScale={2}
                fixedDecimalScale
              />
            </Typography>
          )}
          <Typography
            style={{
              ...styles.baseFont,
              ...styles.content,
              ...styles.date,
              marginTop: "8px",
            }}
          >
            {moment(isRsp ? processingDate : submittedDate)
              .utcOffset("+0800")
              .format(
                isRsp || isManBuy ? "DD MMM YYYY" : "DD MMM YYYY, h:mm a"
              )}
          </Typography>
        </StepContent>
      </Step>

      {/* Processing */}
      <Step active={isProcessing}>
        <StepLabel
          icon={<DoneIcon active={isProcessing} />}
          sx={{ padding: "0" }}
        >
          <Typography
            color="textPrimary"
            style={{
              ...styles.baseFont,
              ...styles.label,
              color: isProcessing ? "#000000" : "#9D9EA4",
            }}
          >
            Processing
          </Typography>
        </StepLabel>
        <StepContent
          sx={{
            borderColor: isProcessing && "#0091DA",
            borderWidth: 2,
            height: "100%",
            paddingBottom: "24px",
          }}
        >
          {isRsp && (
            <Typography style={{ ...styles.baseFont, ...styles.content }}>
              Your Auto Cash In amount is being processed
            </Typography>
          )}
          <Typography
            style={{
              ...styles.baseFont,
              ...styles.content,
              ...styles.date,
              marginTop: "8px",
            }}
          >
            {processingDate !== null &&
              moment(processingDate)
                .utcOffset("+0800")
                .format(
                  isRsp || isManBuy ? "DD MMM YYYY" : "DD MMM YYYY, h:mm a"
                )}
          </Typography>
        </StepContent>
      </Step>

      {/* Completed */}
      <Step active={isCompleted}>
        <StepLabel icon={completed.icon} sx={{ padding: "0" }}>
          <Typography
            color="textPrimary"
            style={{
              ...styles.baseFont,
              ...styles.label,
              color:
                status === "5" || status === "9" || status === "10"
                  ? "#000000"
                  : "#9D9EA4",
            }}
          >
            {completed.text}
          </Typography>
        </StepLabel>
        <StepContent
          style={{
            borderColor: isCompleted && "#0091DA",
            borderWidth: 2,
            marginTop: "0",
            height: "100%",
            paddingBottom:
              ((details.length > 0 && details[0].adjustments.length > 0) ||
                (Array.isArray(corrections) && corrections.length > 0)) &&
              "24px",
          }}
        >
          <>
            <Typography style={{ ...styles.baseFont, ...styles.content }}>
              {isFailed
                ? `${frontText} Cash In is unsuccessful.`
                : isRsp &&
                  "Your Auto Cash In amount is successful and has been reflected in your account"}
            </Typography>
            {isFailed ? (
              <Typography
                style={{
                  ...styles.baseFont,
                  ...styles.content,
                  ...styles.date,
                  marginTop: "8px",
                }}
              >
                {completedDate !== null &&
                  moment(completedDate)
                    .utcOffset("+0800")
                    .format("DD MMM YYYY")}
              </Typography>
            ) : (
              <>
                {!isRsp && (
                  <SalesCharge
                    amount={amount}
                    fee={fee}
                    salesChargeAmount={salesChargeAmount}
                    salesChargeCcy={salesChargeCcy}
                    salesChargePercent={salesChargePercent}
                    serviceTaxAmount={serviceTaxAmount}
                    serviceTaxRate={serviceTaxRate}
                    isRetire={isRetire}
                    amountOnly={amountOnly}
                    ppaFee={ppaFee}
                  />
                )}
                <div
                  style={{
                    marginTop: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography style={{ ...styles.baseFont, ...styles.content }}>
                    Unit: {unit}
                  </Typography>
                  <Typography
                    style={{
                      ...styles.baseFont,
                      ...styles.content,
                      textAlign: "right",
                    }}
                  >
                    Unit Price:{" "}
                    <NumberFormat
                      prefix="RM"
                      value={
                        details.length > 0 &&
                        details[0].actualUnitPrice !== null
                          ? details[0].actualUnitPrice
                          : parseFloat(amount / unit)
                      }
                      thousandSeparator
                      displayType="text"
                      decimalScale={4}
                      fixedDecimalScale
                    />
                  </Typography>
                </div>
              </>
            )}

            {(isFailed || status === "5") && (
              <Typography
                style={{
                  ...styles.baseFont,
                  ...styles.content,
                  ...styles.date,
                  marginTop: "8px",
                }}
              >
                {moment(completedDate)
                  .utcOffset("+0800")
                  .format("DD MMM YYYY")}
              </Typography>
            )}
          </>
        </StepContent>
      </Step>

      {Array.isArray(corrections) &&
        corrections.map((item, id) => (
          <React.Fragment key={id}>
            {item.reversal && (
              <Step active>
                <StepLabel icon={<DoneIcon active />} sx={{ padding: "0" }}>
                  <Typography
                    color="textPrimary"
                    style={{
                      ...styles.baseFont,
                      ...styles.label,
                      color: "#000000",
                    }}
                  >
                    Reverse Cash In
                  </Typography>
                </StepLabel>
                <StepContent
                  sx={
                    item.hasOwnProperty("correction")
                      ? {
                          borderColor: "#0091DA",
                          borderWidth: 2,
                          marginTop: "0",
                          height: "100%",
                          paddingBottom: "24px",
                        }
                      : { border: "none" }
                  }
                >
                  <Typography
                    style={{
                      ...styles.baseFont,
                      ...styles.content,
                      ...styles.date,
                      marginTop: "8px",
                    }}
                  >
                    {moment(item.reversal.navDate)
                      .utcOffset("+0800")
                      .format("DD MMM YYYY")}
                  </Typography>
                </StepContent>
              </Step>
            )}
            {item.correction && (
              <Step active>
                <StepLabel icon={<DoneIcon active />}>
                  <Typography
                    color="textPrimary"
                    style={{
                      ...styles.baseFont,
                      ...styles.label,
                      color: "#000000",
                    }}
                  >
                    Adjusted Cash In
                  </Typography>
                </StepLabel>
                <StepContent
                  style={
                    id !== corrections.length - 1
                      ? {
                          borderColor: "#0091DA",
                          borderWidth: "2px",
                          height: "100%",
                          marginTop: "0",
                          paddingBottom: "24px",
                        }
                      : { border: "none" }
                  }
                >
                  <div
                    style={{
                      marginTop: "3px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                      }}
                    >
                      Request Cash In
                    </Typography>
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                      }}
                    >
                      RM{" "}
                      <NumberFormat
                        value={amount + fee}
                        thousandSeparator
                        displayType="text"
                        decimalScale={2}
                        fixedDecimalScale
                      />
                    </Typography>
                  </div>
                  <div
                    style={{
                      marginTop: "3px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                      }}
                    >
                      Sales Charge ({item.correction?.salesChargePercent || 0}
                      %)
                    </Typography>
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                      }}
                    >
                      <NumberFormat
                        prefix="RM"
                        value={item.correction.actualAmt}
                        thousandSeparator
                        displayType="text"
                        decimalScale={2}
                        fixedDecimalScale
                      />
                    </Typography>
                  </div>
                  {/* START: SST */}
                  <div
                    style={{
                      marginTop: "3px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                      }}
                    >
                      SST ({item.correction?.serviceTaxRate || 0}%)
                    </Typography>
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                      }}
                    >
                      <NumberFormat
                        prefix="RM"
                        value={item.correction?.serviceTaxAmount || 0}
                        thousandSeparator
                        displayType="text"
                        decimalScale={2}
                        fixedDecimalScale
                      />
                    </Typography>
                  </div>
                  {/* END: SST */}
                  <div
                    style={{
                      marginTop: "3px",
                      display: "flex",
                      justifyContent: "space-between",
                      fontWeight: "700",
                      color: "#616367",
                    }}
                  >
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                        fontWeight: "inherit",
                        color: "inherit",
                      }}
                    >
                      Net amount
                    </Typography>
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                        fontWeight: "inherit",
                        color: "inherit",
                      }}
                    >
                      <NumberFormat
                        prefix="RM"
                        value={
                          item.correction?.actualAmt -
                          item.correction?.salesChargeAmount
                        }
                        thousandSeparator
                        displayType="text"
                        decimalScale={2}
                        fixedDecimalScale
                      />
                    </Typography>
                  </div>
                  <div
                    style={{
                      marginTop: "8px",
                      display: "flex",
                      justifyContent: "space-between"
                    }}
                  >
                    <Typography
                      style={{ ...styles.baseFont, ...styles.content }}
                    >
                      Unit: {item.correction.unit}
                    </Typography>
                    <Typography
                      style={{ ...styles.baseFont, ...styles.content, textAlign: "right" }}
                    >
                      Unit Price:{" "}
                      <NumberFormat
                        prefix="RM"
                        value={
                          item.correction.actualUnitPrice ||
                          parseFloat(
                            item.correction.amount / item.correction.unit
                          )
                        }
                        thousandSeparator
                        displayType="text"
                        decimalScale={4}
                        fixedDecimalScale
                      />
                    </Typography>
                  </div>
                  <Typography
                    style={{
                      ...styles.baseFont,
                      ...styles.content,
                      ...styles.date,
                      marginTop: "8px",
                    }}
                  >
                    {moment(item.correction.navDate)
                      .utcOffset("+0800")
                      .format("DD MMM YYYY")}
                  </Typography>
                </StepContent>
              </Step>
            )}
          </React.Fragment>
        ))}
    </TransactionHistoryBase>
  );
};

export default Buy;
